import { useEffect, useState } from "react";

import { DateTimePicker } from "@hl/base-components/lib/DateTimePicker";
import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import { STATUS_COLOR } from "@hl/base-components/lib/theme/colors";
import {
  _CollectionType,
  TransactionType,
} from "@hl/shared-features/lib/apollo/graphql.generated";
import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import {
  TransactionStateType,
  useTransactionsDispatch,
  useTransactionState,
} from "@hl/shared-features/lib/features/evm-tx/TransactionContext";
import CryptoCurrencyInput from "@hl/shared-features/lib/features/input/CryptoCurrencyInput";
import { logError } from "@hl/shared-features/lib/services/logger";
import { Box, Button, Group, Modal, Space, Text } from "@mantine/core";
import { useForm } from "@mantine/form";

import GateSelect from "components/GateSelect";
import { useGatesQuery } from "~/features/gates/queries/gates.graphql.generated";
import {
  GetManageTokenAuctionsQuery,
  useUpdateAuctionMutation,
} from "~/features/tokens/sections/ManageToken/queries.graphql.generated";

/* eslint-disable  @typescript-eslint/no-explicit-any */
type Props = {
  collectionId: string;
  auction: GetManageTokenAuctionsQuery["getAuctionsByCollection"][0];
  isOpened: boolean;
  onClose: () => void;
  refetchCollection: any;
  refetchAuction: any;
  enableEdit: boolean;
};

interface EditAuctionFormValues {
  startDate: Date;
  endDate?: Date;
  price: number;
  gateId: string;
}

export const EditAuction = ({
  isOpened,
  onClose,
  auction,
  collectionId,
  refetchCollection,
  refetchAuction,
  enableEdit,
}: Props) => {
  const { walletAddress } = useAuth();
  const getNewTxnId = () => `txn-${Date.now()}`;
  const [error, setError] = useState<string>("");
  const [txnId, setTxnId] = useState<string>(getNewTxnId());

  const transactionState = useTransactionState(txnId);
  const transactionDispatch = useTransactionsDispatch();

  const [updateAuction, { data, loading }] = useUpdateAuctionMutation();

  const refetch = () => {
    refetchCollection();
    refetchAuction();
  };
  const { data: gatesData, loading: gatesLoading } = useGatesQuery();

  useEffect(() => {
    if (data && auction?.id && walletAddress && data.auctionUpdate.contract) {
      transactionDispatch?.({
        type: "UPDATE_TX_ARGS",
        payload: {
          id: txnId,
          args: data.auctionUpdate.contract,
          metadata: {
            auctionId: auction.id,
            collectionId: collectionId,
            nftContractAddress: walletAddress,
          },
        },
      });
    }
  }, [data, auction?.id]);

  const form = useForm<EditAuctionFormValues>({
    initialValues: {
      startDate: new Date(auction.start),
      endDate: new Date(auction.end),
      price: parseFloat(auction.price),
      gateId: auction.gateId ?? "",
    },
    validate: (values) => ({
      price: values.price === null ? "Price is required" : null,
      startDate: !values.startDate
        ? "Sale start date is required"
        : values.endDate && values.endDate < values.startDate
        ? "Sale start date must be less than or equal to sale end date"
        : null,
      endDate:
        values.endDate && values.endDate < values.startDate
          ? "Sale end date must be greater than or equal to sale start date"
          : null,
    }),
  });

  const closeModal = () => {
    refetch();
    form.reset();
    onClose();
  };

  useEffect(() => {
    if (data && !data.auctionUpdate.contract) {
      closeModal();
    }
  }, [data]);

  const onSubmit = form.onSubmit(async (values) => {
    setError("");

    if (!auction?.id) {
      throw new Error("Auction without id");
    }

    try {
      const submission = {
        auctionId: auction.id!,
        start: values.startDate.toString(),
        end: values.endDate?.toString() || null,
        price: values.price.toString(),
        gateId: values.gateId ?? "",
      };

      await updateAuction({ variables: { data: submission } });
      const txnId = getNewTxnId();
      transactionDispatch?.({
        type: "START_TRANSACTION",
        payload: {
          id: txnId,
          entityId: auction.id!,
          transactionType: TransactionType.EVM_721_AUCTION,
          collectionType: _CollectionType.OneOfOne,
        },
      });
      setTxnId(txnId);
    } catch (e) {
      setError("Failed to save changes.");
      logError({
        e,
        message: "[EditAuctionModal] Failed to update auction",
      });
    }
  });

  useEffect(() => {
    form.validate();
  }, [form.values]);

  let buttonLabel = "Save Changes";
  let buttonDisabled = false;
  if (transactionState && !transactionState.error && !!transactionState.args) {
    if (transactionState.type === TransactionStateType.WaitingSignedTx) {
      buttonLabel = "Waiting for user signature...";
      buttonDisabled = false;
    } else if (transactionState.type !== TransactionStateType.Done) {
      buttonLabel = "Updating your auction...";
      buttonDisabled = true;
    }
  }

  useEffect(() => {
    if (
      transactionState &&
      transactionState.type === TransactionStateType.Done
    ) {
      closeModal();
    }
  }, [transactionState]);

  return (
    <>
      <Modal
        size="lg"
        opened={isOpened}
        onClose={closeModal}
        title="Edit Auction"
      >
        <Box mx="auto">
          <form onSubmit={onSubmit}>
            <CryptoCurrencyInput
              label="Reserve Price"
              placeholder="Token price"
              chainId={auction.chainId}
              {...form.getInputProps("price")}
            />
            <Space h="lg" />
            <DateTimePicker
              dropdownType="modal"
              label={
                <>
                  <Group spacing={4}>
                    <Text>Auction start date</Text>
                  </Group>
                </>
              }
              clearable={false}
              {...form.getInputProps("startDate")}
              minDate={new Date()}
            />
            <Space h="lg" />
            <DateTimePicker
              dropdownType="modal"
              label={
                <>
                  <Group spacing={4}>
                    <Text>Auction end date</Text>
                  </Group>
                </>
              }
              placeholder="None"
              {...form.getInputProps("endDate")}
            />
            <Space h="lg" />
            <GateSelect
              description="Select a gate to determine who can bid on this collection"
              gates={gatesData?.gates ?? []}
              value={auction.gateId ?? ""}
              onChange={(newGateId: string) =>
                form.setValues({ gateId: newGateId })
              }
              isGatingMint={false}
              isGatingAuction
            />
            <ModalSection>
              <Button
                type="submit"
                size="xl"
                fullWidth
                loading={loading}
                disabled={buttonDisabled || gatesLoading || !enableEdit}
              >
                {buttonLabel}
              </Button>
              {error && (
                <Text size="xs" mt={5} color={STATUS_COLOR.ERROR}>
                  {error}
                </Text>
              )}
            </ModalSection>
          </form>
        </Box>
      </Modal>
    </>
  );
};
